import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbstractItemComponent } from '@df/catalog-ui/abstract-item.component';
import { Listing } from '@df/catalog-ui/listing/listing';
import { IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { LocalICmsPage } from '../../../../catalog/page/local/local-page';
import { StickyFreeComponent } from '../../../../common/sticky-free/sticky-free.component';
import { ActionComponent } from '../../../../ui/api/action.component';
import { Subscription } from 'rxjs';
import { NBlogCategoryPageType } from '../../page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../../page/post-view/cms-blog-page-post-view.component';
import { CmsBlogContentBlogCardComponent } from '../blog-card/cms-blog-content-blog-card.component';
import { CmsBlogContentTagFilter } from '../tag-filter/cms-blog-content-tag-filter';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';

interface INavigationItem {
    url: string;
    name: string;
    id?: number;
}

@Component({
    selector: 'cms-blog-content-post-listing',
    templateUrl: './cms-blog-content-post-listing.html',
    styleUrls: ['./cms-blog-content-post-listing.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        IfSizeDirective,
        CommonModule,
        RouterLink,
        TranslationModule,
        ActionComponent,
        CmsBlogContentBlogCardComponent,
        GridModule,
        StickyFreeComponent,
        ZippyModule
    ]
})
export class CmsBlogContentPostListingViewComponent extends AbstractItemComponent<
    LocalICmsPage<IPageDataV3<NBlogCategoryPageType.ITypeData>>
> {
    type = ICmsPage.type;

    translationService = inject(TranslationService);

    /**
     * Listing subscfiptions
     */
    private listingSubscription?: Subscription;

    protected isNavMenuOpen = false;
    /**
     * Children posts listing
     */
    listing?: Listing<LocalICmsPage<IPageDataV2<NBlogPostPageType.ITypeData>>>;

    /**
     * Blog page origin
     */
    readonly blogOrigin = NBlogCategoryPageType.blogOrigin;

    /**
     * Type data getter shorthand
     */
    get typeData() {
        return this.model?.data?.typeData;
    }

    /**
     * Parent page type data shorthand
     */
    private get parentTypeData() {
        return (this.model?.parent?.data as IPageDataV3<NBlogCategoryPageType.ITypeData>)?.typeData;
    }
    /**
     * Sibling categories
     */
    private siblingsOrChildren?: LocalICmsPage<IPageDataV2>[];

    /**
     * Setup listing on model updates
     */

    protected override onModelChange(): void {
        super.onModelChange();
        this.listingSubscription?.unsubscribe();
        this.listing?.clearSubscriptions();
        this.listing = new Listing<LocalICmsPage<IPageDataV2<NBlogPostPageType.ITypeData>>>(
            {
                type: 'page',
                storage: true,
                infScrollInit: [4, 8, 8, 8],
                infScrollStep: [2, 4, 4, 4]
            },
            this.model?.getBlogCategoryPosts() as LocalICmsPage<IPageDataV3<NBlogPostPageType.ITypeData>>[]
        );
        this.listing.setSortOptions([{ label: 'Date', code: 'typeData.date', desc: true, default: true }]);
        this.listing.addFilter(new CmsBlogContentTagFilter({ id: 'tags', isOpen: true, position: 0, type: 'custom' }, this.listing));
        this.listing.applyFilters();
        const parentId = this.model?.data?.parent || this.model?.id;
        this.siblingsOrChildren = this.app.resources.page
            .getAllExisting()
            .filter(i => i.data?.pageType === NBlogCategoryPageType.typeId && i.data.parent === parentId) as LocalICmsPage<IPageDataV2>[];
        this.toggleNavMenu(false, true);
        this.listing.subscribe(() => this.detectChanges());
    }

    get navigationItems(): INavigationItem[] {
        return [
            { name: this.translationService.translate('All articles'), url: this.blogOrigin },
            ...(this.siblingsOrChildren?.map(i => ({
                name: i.name,
                url: i.data?.url || '',
                id: i.id
            })) || [])
        ];
    }

    get currentCategoryName(): string {
        const item = this.navigationItems.find(item => this.isSelected(item));
        return (item?.id && item.name) || this.translationService.translate('All articles');
    }

    get tagFilter() {
        return this.listing?.filtersMap['tags'] as CmsBlogContentTagFilter;
    }

    get name() {
        return this.typeData?.useParent ? this.model?.parent?.data?.name : this.model?.data?.name;
    }

    get topCopy() {
        return this.typeData?.useParent ? this.parentTypeData?.topCopy : this.typeData?.topCopy;
    }

    /**
     * No option passed for 'view all'
     */
    isSelected(option: INavigationItem): boolean {
        if (this.typeData?.tagRedirect) {
            return option.id ? option.id === this.model?.id : this.model?.data?.url === NBlogCategoryPageType.blogOrigin;
        }
        return option.id ? !!this.tagFilter.isSelected(option.id) : !this.tagFilter.hasSelection;
    }

    /**
     * No option passed for 'view all'
     */
    selectNav(option: INavigationItem) {
        if (!this.typeData?.tagRedirect) {
            if (option.id) {
                this.tagFilter.select(option.id);
            } else {
                this.tagFilter.reset();
            }
        }
        this.toggleNavMenu(false);
    }

    toggleNavMenu(to = !this.isNavMenuOpen, silent = false) {
        if (this.isNavMenuOpen !== to) {
            this.isNavMenuOpen = to;
            if (!silent) {
                this.detectChanges();
            }
        }
    }
}
